import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toaster } from "baseui/toast";
import { defaultInstance } from "../api/httpClient";
import { fetchAuthSession } from "../utils/session";
import { useState, useEffect } from "react";

export interface Tenant {
  id: string;
  name: string;
  domain?: string;
  settings: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

// Hook to get tenant ID(s) from JWT token
export const useTenantIds = () => {
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getTenantIds = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        
        if (idToken) {
          const tokenParts = idToken.split('.');
          if (tokenParts.length === 3) {
            const tokenPayload = JSON.parse(atob(tokenParts[1]));
            
            // Get tenant_ids (multiple) or tenant_id (single)
            let tenantIdsValue = tokenPayload['custom:tenant_ids'];
            const tenantIdValue = tokenPayload['custom:tenant_id'];
            
            let ids: string[] = [];
            
            if (tenantIdsValue) {
              // tenant_ids can be:
              // 1. An array: ["uuid1", "uuid2"]
              // 2. A JSON string: '["uuid1", "uuid2"]'
              // 3. A comma-separated string: "uuid1,uuid2"
              
              if (typeof tenantIdsValue === 'string') {
                // Try to parse as JSON first
                try {
                  const parsed = JSON.parse(tenantIdsValue);
                  if (Array.isArray(parsed)) {
                    ids = parsed.map((id: any) => String(id).trim());
                  } else {
                    // Not a JSON array, try comma-separated
                    ids = tenantIdsValue.split(',').map((id: string) => id.trim());
                  }
                } catch {
                  // Not valid JSON, treat as comma-separated
                  ids = tenantIdsValue.split(',').map((id: string) => id.trim());
                }
              } else if (Array.isArray(tenantIdsValue)) {
                // Already an array
                ids = tenantIdsValue.map((id: any) => String(id).trim());
              } else {
                // Fallback: convert to string
                ids = [String(tenantIdsValue)];
              }
            } else if (tenantIdValue) {
              // Single tenant - ensure it's a string, not an array
              let singleId: string;
              if (Array.isArray(tenantIdValue)) {
                singleId = String(tenantIdValue[0]);
              } else if (typeof tenantIdValue === 'string') {
                singleId = tenantIdValue;
              } else {
                singleId = String(tenantIdValue);
              }
              ids = [singleId];
            }
            
            setTenantIds(ids);
            
            // Clear corrupted sessionStorage if it exists
            const storedTenantId = sessionStorage.getItem('selectedTenantId');
            
            // Check if stored ID is corrupted (contains brackets)
            if (storedTenantId && (storedTenantId.includes('[') || storedTenantId.includes(']'))) {
              sessionStorage.removeItem('selectedTenantId');
            }
            
            let finalTenantId: string;
            const cleanStoredId = sessionStorage.getItem('selectedTenantId');
            
            if (cleanStoredId && ids.includes(cleanStoredId)) {
              finalTenantId = cleanStoredId;
            } else if (ids.length > 0) {
              finalTenantId = ids[0];
            } else {
              return;
            }
            
            setCurrentTenantId(finalTenantId);
            sessionStorage.setItem('selectedTenantId', finalTenantId);
          }
        }
      } catch (error) {
        console.error('Error getting tenant IDs from token:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    getTenantIds();
  }, []);

  const switchTenant = (tenantId: string) => {
    if (tenantIds.includes(tenantId)) {
      setCurrentTenantId(tenantId);
      sessionStorage.setItem('selectedTenantId', tenantId);
    }
  };

  return {
    tenantIds,
    currentTenantId,
    isLoading,
    switchTenant,
    hasMultipleTenants: tenantIds.length > 1,
  };
};

// Hook to manage tenant CRUD operations
export const useTenant = () => {
  const queryClient = useQueryClient();
  const { currentTenantId, tenantIds, isLoading: isLoadingIds, switchTenant, hasMultipleTenants } = useTenantIds();

  // Fetch tenant data
  const { data: tenant, isLoading: isLoadingTenant, error } = useQuery({
    queryKey: ['tenant', currentTenantId],
    queryFn: async () => {
      if (!currentTenantId) {
        throw new Error('No tenant ID available');
      }

      const response = await defaultInstance({
        method: 'GET',
        url: `/tenant/${currentTenantId}`,
      });

      // The API might return an array or a single object
      const tenantData = Array.isArray(response.data) ? response.data[0] : response.data;
      
      return tenantData as Tenant;
    },
    enabled: !!currentTenantId,
  });

  // Fetch all tenants if user has multiple
  const { data: allTenants } = useQuery({
    queryKey: ['tenants', tenantIds],
    queryFn: async () => {
      if (tenantIds.length === 0) {
        return [];
      }

      // Fetch all tenants
      const promises = tenantIds.map(id =>
        defaultInstance({
          method: 'GET',
          url: `/tenant/${id}`,
        })
      );

      const responses = await Promise.all(promises);
      return responses.map(response => 
        Array.isArray(response.data) ? response.data[0] : response.data
      ) as Tenant[];
    },
    enabled: tenantIds.length > 0,
  });

  // Update tenant mutation
  const updateTenantMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Tenant> }) => {
      const response = await defaultInstance({
        method: 'PUT',
        url: `/tenant/${id}`,
        data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toaster.positive("Tenant updated successfully", {
        autoHideDuration: 3000,
      });
    },
    onError: (error: any) => {
      console.error('Error updating tenant:', error);
      toaster.negative(error?.response?.data?.message || "Error updating tenant", {
        autoHideDuration: 3000,
      });
    },
  });

  // Patch tenant mutation
  const patchTenantMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Tenant> }) => {
      const response = await defaultInstance({
        method: 'PATCH',
        url: `/tenant/${id}`,
        data,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toaster.positive("Tenant updated successfully", {
        autoHideDuration: 3000,
      });
    },
    onError: (error: any) => {
      console.error('Error updating tenant:', error);
      toaster.negative(error?.response?.data?.message || "Error updating tenant", {
        autoHideDuration: 3000,
      });
    },
  });

  return {
    tenant,
    allTenants,
    tenantIds,
    currentTenantId,
    hasMultipleTenants,
    isLoading: isLoadingIds || isLoadingTenant,
    error,
    updateTenant: (id: string, data: Partial<Tenant>, options?: { onSuccess?: () => void }) =>
      updateTenantMutation.mutate({ id, data }, options),
    patchTenant: (id: string, data: Partial<Tenant>) => patchTenantMutation.mutate({ id, data }),
    isUpdating: updateTenantMutation.isPending || patchTenantMutation.isPending,
    switchTenant,
  };
};
